import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Array } from '@blade/std-lib'
import {
  ActivePremiumListItemSkeleton,
  ExpiredPremiumListItemSkeleton,
} from '@root/entities/premium/ui/desktop'
import { useAppRoutes } from '@root/features/routing'
import { InfoMessage, PremiumIcon } from '@root/shared/ui-kit'

import type { UserPremiumTab } from '../../premium-tabs'

type UserPremiumListEmptyProps = {
  placement?: 'profile' | 'premium'
  tab?: UserPremiumTab
}

export const UserPremiumListEmpty: React.FC<UserPremiumListEmptyProps> = ({
  placement = 'premium',
  tab = 'active',
}) => {
  const { t } = useTranslation()
  const { navigateToRoot } = useAppRoutes()

  const handleExploreTokens = () => navigateToRoot()

  if (placement === 'profile') {
    return (
      <Root>
        <InfoMessage
          icon={<PremiumIcon />}
          title={t('premium.purchased.empty-title')}
          description={t('premium.purchased.empty-description')}
          intent="empty"
          variant="bold"
          retryText={t('common.explore-tokens')}
          onRetry={handleExploreTokens}
          withBackground
        />
      </Root>
    )
  }

  const SkeletonCmp =
    tab === 'active'
      ? ActivePremiumListItemSkeleton
      : ExpiredPremiumListItemSkeleton

  return (
    <Root>
      <InfoContainer>
        <InfoMessage
          icon={<PremiumIcon />}
          title={t('premium.purchased.empty-title')}
          description={t('premium.purchased.empty-description')}
          intent="empty"
          variant="bold"
          retryText={t('common.explore-tokens')}
          onRetry={handleExploreTokens}
          css={css`
            z-index: 2;
          `}
        />
      </InfoContainer>
      <Background>
        {Array.range(1, 10).map((it) => (
          <SkeletonCmp key={it} disabled />
        ))}
      </Background>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  position: relative;
`

const Background = styled.div`
  position: absolute;
  left: 0;
  top: 3px;
  bottom: 0;
  width: 100%;

  display: grid;
  padding: 12px;
  align-items: start;
  gap: 10px;
  align-content: flex-start;
  opacity: 0.5;

  &::after {
    content: '';

    background: linear-gradient(0deg, var(--appearance-1) 0%, transparent 100%);

    position: absolute;
    bottom: 0;
    left: 0;
    top: 0;
    width: 100%;
  }
`

const InfoContainer = styled.div`
  display: grid;
`
